import { usePrices } from '../hooks/use-prices'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Decimal from 'decimal.js'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import sechLogo from '@/assets/logo.png'
import ethLogo from '@/assets/crypto-eth.svg'
import usdtLogo from '@/assets/crypto-usdt.svg'

function formatPrice(price: number): string {
	const decimals = IS_TESTNET ? 6 : 2
	return Number(new Decimal(price).toFixed(decimals)).toLocaleString('en-US', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals
	})
}

interface TokenRowProps {
	logo: string
	symbol: string
	subtitle: string
	price: number
	change24h?: number | null
}

function TokenRow({ logo, symbol, subtitle, price, change24h }: TokenRowProps) {
	const hasChange = change24h != null
	const rounded = hasChange ? Math.round(change24h * 100) / 100 : 0
	const isPositive = hasChange && rounded > 0
	const isNegative = hasChange && rounded < 0

	return (
		<div className="flex items-center justify-between px-3 py-2.5 bg-[#212124] rounded-xl">
			{/* Left: logo + name */}
			<div className="flex items-center gap-2.5 min-w-0">
				<img
					src={logo}
					alt={symbol}
					className="shrink-0 rounded-full object-cover h-7 w-7"
				/>

				<div className="flex flex-col min-w-0">
					<span className="text-xs font-semibold font-figtree text-white leading-4">{symbol}</span>
					<span className="text-[10px] font-figtree text-[#9CA3AF] leading-3 truncate">{subtitle}</span>
				</div>
			</div>

			{/* Right: price + change */}
			<div className="flex items-center gap-2 shrink-0">
				<span className="text-sm font-semibold font-figtree text-white">
					${formatPrice(price)}
				</span>

				{hasChange && (
					<div className="flex items-center gap-0.5">
						{isPositive && <TrendingUp className="w-3 h-3 text-green-400" />}
						{isNegative && <TrendingDown className="w-3 h-3 text-red-400" />}
						<span className={`text-[11px] font-semibold font-figtree ${
							isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-[#9CA3AF]'
						}`}>
							{isPositive ? '+' : ''}{change24h!.toFixed(2)}%
						</span>
					</div>
				)}
			</div>
		</div>
	)
}

export default function PriceTracker() {
	const { prices, isLoading, error } = usePrices()

	if (error) {
		return (
			<Alert variant="destructive">
				<AlertCircle className="h-4 w-4" />
				<AlertDescription>
					Failed to fetch prices. Using fallback values.
				</AlertDescription>
			</Alert>
		)
	}

	if (isLoading) {
		return (
			<div className="flex flex-col gap-2">
				{Array(3).fill(0).map((_, i) => (
					<Skeleton key={i} className="h-[52px] rounded-xl" />
				))}
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-2">
			{prices?.sech != null && (
				<TokenRow
					logo={sechLogo}
					symbol="SECH"
					subtitle="Secrets Token"
					price={prices.sech.price}
					change24h={prices.sech.change24h}
				/>
			)}

			{prices?.eth != null && (
				<TokenRow
					logo={ethLogo}
					symbol="ETH"
					subtitle="Ethereum"
					price={prices.eth.price}
					change24h={prices.eth.change24h}
				/>
			)}

			{prices?.usdc != null && (
				<TokenRow
					logo={usdtLogo}
					symbol="USDT"
					subtitle="Tether USD"
					price={prices.usdc.price}
					change24h={prices.usdc.change24h}
				/>
			)}
		</div>
	)
}
